import { useState } from 'react'
import {
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Textarea,
  Checkbox,
  Radio,
  Badge,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Modal,
  Spinner,
  Skeleton,
  ProgressBar,
  Chat,
  AISuggestionsPanel,
  ThemeToggle,
  useToast,
  type Message,
  type AISuggestion,
} from '@/components/ui'
import { HomeIcon, UsersIcon, ChartBarIcon } from '@heroicons/react/24/outline'

function ComponentShowcase() {
  const [modalOpen, setModalOpen] = useState(false)
  const [chatMessages, setChatMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! How can I assist you today?',
      timestamp: new Date(),
    },
  ])
  const { addToast } = useToast()

  const handleSendMessage = (content: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    }
    setChatMessages(prev => [...prev, newMessage])
    
    // Simulate AI response
    setTimeout(() => {
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I received your message: "' + content + '"',
        timestamp: new Date(),
      }
      setChatMessages(prev => [...prev, aiMessage])
    }, 1000)
  }

  const aiSuggestions: AISuggestion[] = [
    {
      id: '1',
      title: 'Complete vendor evaluation',
      description: 'You have 3 pending vendor evaluations that need review.',
      actions: [
        { label: 'Review Now', onClick: () => addToast({ variant: 'info', title: 'Opening evaluations...' }), variant: 'primary' },
        { label: 'Remind Me Later', onClick: () => addToast({ variant: 'success', title: 'Reminder set' }), variant: 'secondary' },
      ],
    },
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground">
            Overmatch Component Showcase
          </h1>
          <ThemeToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="buttons">
          <TabsList>
            <TabsTrigger value="buttons">Buttons & Forms</TabsTrigger>
            <TabsTrigger value="data">Data Display</TabsTrigger>
            <TabsTrigger value="feedback">Feedback</TabsTrigger>
            <TabsTrigger value="ai">AI Components</TabsTrigger>
          </TabsList>

          {/* Buttons & Forms Tab */}
          <TabsContent value="buttons">
            <div className="space-y-8">
              {/* Buttons Section */}
              <Card>
                <h3 className="text-lg font-semibold mb-4">Buttons</h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="default">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="success">Success</Button>
                  <Button variant="destructive">Danger</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="default" isLoading>Loading</Button>
                  <Button variant="default" disabled>Disabled</Button>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button variant="default" size="sm">Small</Button>
                  <Button variant="default">Medium</Button>
                  <Button variant="default" size="lg">Large</Button>
                </div>
              </Card>

              {/* Form Elements */}
              <Card>
                <h3 className="text-lg font-semibold mb-4">Form Elements</h3>
                <div className="space-y-4 max-w-md">
                  <Input
                    label="Email"
                    type="email"
                    placeholder="your@email.com"
                    helpText="We'll never share your email"
                  />
                  <Input
                    label="Password"
                    type="password"
                    error="Password must be at least 8 characters"
                  />
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea
                    label="Description"
                    placeholder="Enter description..."
                    rows={3}
                  />
                  <Checkbox label="I agree to the terms and conditions" />
                  <div className="space-y-2">
                    <Radio name="priority" label="High Priority" />
                    <Radio name="priority" label="Medium Priority" />
                    <Radio name="priority" label="Low Priority" />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Data Display Tab */}
          <TabsContent value="data">
            <div className="space-y-8">
              {/* Badges */}
              <Card>
                <h3 className="text-lg font-semibold mb-4">Badges</h3>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="pending">Pending</Badge>
                  <Badge variant="in-progress">In Progress</Badge>
                  <Badge variant="approved">Approved</Badge>
                  <Badge variant="rejected">Rejected</Badge>
                  <Badge variant="warning">Warning</Badge>
                  <Badge variant="info">Info</Badge>
                </div>
              </Card>

              {/* Table */}
              <Card>
                <h3 className="text-lg font-semibold mb-4">Table</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead sortable sorted="asc">Vendor Name</TableHead>
                      <TableHead sortable>Status</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow interactive>
                      <TableCell>Acme Drones Inc.</TableCell>
                      <TableCell><Badge variant="approved">Approved</Badge></TableCell>
                      <TableCell>95/100</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">View</Button>
                      </TableCell>
                    </TableRow>
                    <TableRow interactive>
                      <TableCell>SkyTech Solutions</TableCell>
                      <TableCell><Badge variant="in-progress">In Progress</Badge></TableCell>
                      <TableCell>82/100</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">View</Button>
                      </TableCell>
                    </TableRow>
                    <TableRow interactive>
                      <TableCell>FlyRight Systems</TableCell>
                      <TableCell><Badge variant="pending">Pending</Badge></TableCell>
                      <TableCell>--</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">View</Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>

              {/* Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card interactive>
                  <HomeIcon className="w-8 h-8 text-blue-600 mb-3" />
                  <h4 className="font-semibold mb-2">Active Programs</h4>
                  <p className="text-3xl font-bold text-foreground">24</p>
                </Card>
                <Card interactive>
                  <UsersIcon className="w-8 h-8 text-green-600 mb-3" />
                  <h4 className="font-semibold mb-2">Vendors</h4>
                  <p className="text-3xl font-bold text-foreground">156</p>
                </Card>
                <Card interactive>
                  <ChartBarIcon className="w-8 h-8 text-amber-600 mb-3" />
                  <h4 className="font-semibold mb-2">Submissions</h4>
                  <p className="text-3xl font-bold text-foreground">432</p>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            <div className="space-y-8">
              {/* Toast Examples */}
              <Card>
                <h3 className="text-lg font-semibold mb-4">Toast Notifications</h3>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="success"
                    onClick={() => addToast({ variant: 'success', title: 'Success!', description: 'Operation completed successfully' })}
                  >
                    Show Success
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => addToast({ variant: 'error', title: 'Error!', description: 'Something went wrong' })}
                  >
                    Show Error
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => addToast({ variant: 'info', title: 'Info', description: 'Here is some information' })}
                  >
                    Show Info
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => addToast({ variant: 'warning', title: 'Warning!', description: 'Please proceed with caution' })}
                  >
                    Show Warning
                  </Button>
                </div>
              </Card>

              {/* Modal */}
              <Card>
                <h3 className="text-lg font-semibold mb-4">Modal Dialog</h3>
                <Button variant="default" onClick={() => setModalOpen(true)}>
                  Open Modal
                </Button>
                <Modal
                  isOpen={modalOpen}
                  onClose={() => setModalOpen(false)}
                  title="Example Modal"
                  footer={
                    <>
                      <Button variant="secondary" onClick={() => setModalOpen(false)}>
                        Cancel
                      </Button>
                      <Button variant="default" onClick={() => setModalOpen(false)}>
                        Confirm
                      </Button>
                    </>
                  }
                >
                  <p>This is an example modal dialog. Press Escape to close or click outside.</p>
                </Modal>
              </Card>

              {/* Loading States */}
              <Card>
                <h3 className="text-lg font-semibold mb-4">Loading States</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-2">Spinners</p>
                    <div className="flex items-center gap-4">
                      <Spinner size="sm" />
                      <Spinner size="md" />
                      <Spinner size="lg" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">Skeleton Loaders</p>
                    <div className="space-y-2 max-w-md">
                      <Skeleton variant="text" />
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="rectangular" height="100px" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">Progress Bar</p>
                    <ProgressBar value={65} max={100} />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* AI Components Tab */}
          <TabsContent value="ai">
            <div className="space-y-8">
              {/* AI Suggestions */}
              <Card>
                <h3 className="text-lg font-semibold mb-4">AI Suggestions Panel</h3>
                <AISuggestionsPanel suggestions={aiSuggestions} dismissible />
              </Card>

              {/* Chat Interface */}
              <Card className="h-[500px] p-0 flex flex-col">
                <div className="px-6 py-4 border-b border-border">
                  <h3 className="text-lg font-semibold">Chat Interface</h3>
                </div>
                <div className="flex-1 overflow-hidden">
                  <Chat
                    messages={chatMessages}
                    onSendMessage={handleSendMessage}
                    placeholder="Ask me anything about acquisitions..."
                  />
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  )
}

export default ComponentShowcase

